/**
 * NutritionNotesFeed — "הערות" (nutrition module, Phase 7).
 *
 * A read-only, chronological aggregation of every coach note attached to a
 * single patient's nutrition data: notes written on a menu, on a body
 * measurement, or on one of the recent eating reports. All three sources
 * already expose the same shape — `coachNotes: [{ id, authorId, authorName,
 * content, createdAt }]` with `createdAt` as an ISO string and the text under
 * `content` (NOT `text` — other places in the app get that wrong).
 *
 * This view never writes: adding and deleting a note happens on the item that
 * owns it. Clicking a note's source badge asks the parent to switch tabs.
 *
 * PRIVACY — coach-private data. These notes are written by the clinic team for
 * the clinic team and must NEVER reach the client's public page. The public
 * Cloud Functions pick their response fields explicitly, so `coachNotes` cannot
 * leak through them; do not add a prop, route or serialization path here that
 * would surface this component (or its data) on a public flow.
 *
 * Error codes: CK-10230 (menus load), CK-10231 (measurements load),
 *              CK-10232 (eating reports load).
 */

import { useEffect, useMemo, useState } from "react";
import {
  BowlFood,
  ChatCircleText,
  ForkKnife,
  Ruler,
} from "@phosphor-icons/react";

import { useGetMenusForPatientQuery } from "../../../store/services/menusApi";
import { useGetMeasurementsQuery } from "../../../store/services/measurementsApi";
import { useGetDailyReportsQuery } from "../../../store/services/dailyReportsApi";
import { formatHebrewDate } from "../../../utils/dateUtils";
import EmptyDraw from "../../ui/EmptyDraw";
import "./NutritionNotesFeed.css";
import ContentLoader from "../../shared/ContentLoader";

// ============================================================================
// Constants
// ============================================================================

const DAILY_REPORTS_LIMIT = 30;

/**
 * One descriptor per source. `tab` is the id handed back to `onOpenTab`, and
 * the css modifier drives the shared per-source color tokens (see the css).
 */
const SOURCES = {
  menu: {
    id: "menu",
    tab: "menus",
    label: "תפריט",
    chipLabel: "תפריטים",
    icon: ForkKnife,
  },
  measurement: {
    id: "measurement",
    tab: "measurements",
    label: "מדידה",
    chipLabel: "מדידות",
    icon: Ruler,
  },
  dailyReport: {
    id: "dailyReport",
    tab: "dailyReports",
    label: "דוח אכילה",
    chipLabel: "דוחות אכילה",
    icon: BowlFood,
  },
};

const SOURCE_ORDER = ["menu", "measurement", "dailyReport"];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Midnight of the given instant, in the browser's (clinic's) local time. */
const startOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

// ============================================================================
// Helpers (presentation only)
// ============================================================================

/** ISO string → epoch ms, or null when missing / unparseable. */
const toTimestamp = (isoString) => {
  if (!isoString) return null;
  const ms = new Date(isoString).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/** Hebrew relative label for fresh notes, falling back to a full date. */
const formatNoteDate = (timestamp, rawValue) => {
  if (timestamp === null) return formatHebrewDate(rawValue) || "ללא תאריך";

  const daysAgo = Math.round(
    (startOfDay(Date.now()) - startOfDay(timestamp)) / DAY_MS
  );

  if (daysAgo <= 0) return "היום";
  if (daysAgo === 1) return "אתמול";
  if (daysAgo === 2) return "לפני יומיים";
  if (daysAgo < 7) return `לפני ${daysAgo} ימים`;
  return formatHebrewDate(timestamp);
};

/** Flattens one document's coachNotes into feed entries. */
const collectNotes = (documents, sourceId, getContextLabel) => {
  const source = SOURCES[sourceId];
  const entries = [];

  (documents || []).forEach((doc) => {
    if (!Array.isArray(doc?.coachNotes)) return;
    doc.coachNotes.forEach((note, index) => {
      const content = typeof note?.content === "string" ? note.content.trim() : "";
      if (!content) return;
      const timestamp = toTimestamp(note?.createdAt);
      entries.push({
        key: `${sourceId}-${doc.id || index}-${note?.id || index}`,
        sourceId,
        tab: source.tab,
        sourceLabel: source.label,
        contextLabel: getContextLabel(doc),
        authorName:
          typeof note?.authorName === "string" && note.authorName.trim()
            ? note.authorName.trim()
            : "צוות המרפאה",
        content,
        timestamp,
        dateLabel: formatNoteDate(timestamp, note?.createdAt),
      });
    });
  });

  return entries;
};

// ============================================================================
// Component
// ============================================================================

function NutritionNotesFeed({ clinicId, patientId, onOpenTab }) {
  const [activeSource, setActiveSource] = useState("all");

  const skip = !clinicId || !patientId;

  const {
    data: menus = [],
    isLoading: isLoadingMenus,
    error: menusError,
  } = useGetMenusForPatientQuery({ clinicId, patientId }, { skip });

  const {
    data: measurements = [],
    isLoading: isLoadingMeasurements,
    error: measurementsError,
  } = useGetMeasurementsQuery({ clinicId, patientId }, { skip });

  const {
    data: dailyReports = [],
    isLoading: isLoadingReports,
    error: reportsError,
  } = useGetDailyReportsQuery(
    { clinicId, patientId, limitCount: DAILY_REPORTS_LIMIT },
    { skip }
  );

  // Queries surface errors rather than throw — log them here, without PII.
  useEffect(() => {
    if (!menusError) return;
    console.error("[CK-10230] Error loading menus for notes feed:", {
      errorCode: "CK-10230",
      errorMessage: menusError?.message || "unknown",
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
  }, [menusError, clinicId, patientId]);

  useEffect(() => {
    if (!measurementsError) return;
    console.error("[CK-10231] Error loading measurements for notes feed:", {
      errorCode: "CK-10231",
      errorMessage: measurementsError?.message || "unknown",
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
  }, [measurementsError, clinicId, patientId]);

  useEffect(() => {
    if (!reportsError) return;
    console.error("[CK-10232] Error loading eating reports for notes feed:", {
      errorCode: "CK-10232",
      errorMessage: reportsError?.message || "unknown",
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
  }, [reportsError, clinicId, patientId]);

  const isLoading = isLoadingMenus || isLoadingMeasurements || isLoadingReports;
  const hasError = !!(menusError || measurementsError || reportsError);

  /** Newest first; notes with a missing/broken createdAt sink to the bottom. */
  const notes = useMemo(() => {
    const all = [
      ...collectNotes(menus, "menu", (menu) => menu?.menuName || "תפריט ללא שם"),
      ...collectNotes(
        measurements,
        "measurement",
        (measurement) => formatHebrewDate(measurement?.date) || "ללא תאריך"
      ),
      ...collectNotes(
        dailyReports,
        "dailyReport",
        (report) => formatHebrewDate(report?.date) || "ללא תאריך"
      ),
    ];

    return all.sort((a, b) => {
      if (a.timestamp === null && b.timestamp === null) return 0;
      if (a.timestamp === null) return 1;
      if (b.timestamp === null) return -1;
      return b.timestamp - a.timestamp;
    });
  }, [menus, measurements, dailyReports]);

  const counts = useMemo(() => {
    const result = { all: notes.length, menu: 0, measurement: 0, dailyReport: 0 };
    notes.forEach((note) => {
      result[note.sourceId] += 1;
    });
    return result;
  }, [notes]);

  const visibleNotes = useMemo(
    () =>
      activeSource === "all"
        ? notes
        : notes.filter((note) => note.sourceId === activeSource),
    [notes, activeSource]
  );

  const handleOpenTab = (tabId) => {
    if (!onOpenTab) return;
    onOpenTab(tabId);
  };

  const renderBadge = (note) => {
    const Icon = SOURCES[note.sourceId].icon;
    const text = `${note.sourceLabel} · ${note.contextLabel}`;
    const className = `nnf-badge nnf-badge-${note.sourceId}`;

    if (!onOpenTab) {
      return (
        <span className={className}>
          <Icon size={11} weight="fill" />
          <span className="nnf-badge-text">{text}</span>
        </span>
      );
    }

    return (
      <button
        type="button"
        className={`${className} nnf-badge-link`}
        onClick={() => handleOpenTab(note.tab)}
        title={`מעבר ל${SOURCES[note.sourceId].chipLabel}`}
      >
        <Icon size={11} weight="fill" />
        <span className="nnf-badge-text">{text}</span>
      </button>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <ContentLoader fill message="טוען הערות..." />
      );
    }

    if (notes.length === 0) {
      return (
        <div className="nnf-empty">
          <EmptyDraw variant="form" />
          <p>
            {hasError
              ? "טעינת חלק מההערות נכשלה, נסו לרענן את העמוד"
              : "עדיין אין הערות למטופל זה"}
          </p>
          <span className="nnf-empty-hint">
            הערות שתוסיפו על תפריט, על מדידה או על דוח אכילה יופיעו כאן, מהחדשה
            לישנה.
          </span>
        </div>
      );
    }

    if (visibleNotes.length === 0) {
      return (
        <div className="nnf-empty">
          <EmptyDraw variant="search" />
          <p>אין הערות במקור שנבחר</p>
          <span className="nnf-empty-hint">
            בחרו "הכל" כדי לראות את כל ההערות של המטופל.
          </span>
        </div>
      );
    }

    return (
      <ul className="nnf-list">
        {visibleNotes.map((note) => (
          <li className={`nnf-item nnf-item-${note.sourceId}`} key={note.key}>
            <div className="nnf-item-head">
              <span className="nnf-author">{note.authorName}</span>
              <span className="nnf-date">{note.dateLabel}</span>
            </div>
            <p className="nnf-content">{note.content}</p>
            <div className="nnf-item-foot">{renderBadge(note)}</div>
          </li>
        ))}
      </ul>
    );
  };

  const filterChips = [
    { id: "all", label: "הכל", icon: ChatCircleText },
    ...SOURCE_ORDER.map((sourceId) => ({
      id: sourceId,
      label: SOURCES[sourceId].chipLabel,
      icon: SOURCES[sourceId].icon,
    })),
  ];

  return (
    <div className="nnf-feed">
      <div className="nnf-header">
        <div className="nnf-header-title">
          <span className="nnf-header-icon">
            <ChatCircleText size={20} weight="fill" />
          </span>
          <div className="nnf-header-text">
            <span className="nnf-eyebrow">מעקב תזונתי</span>
            <h3>הערות</h3>
          </div>
        </div>
        <span className="nnf-header-note">
          כל ההערות שנכתבו על תפריטים, מדידות ודוחות אכילה — לעיון בלבד
        </span>
      </div>

      <div className="nnf-card">
        {notes.length > 0 && (
          <div className="nnf-filters" role="group" aria-label="סינון לפי מקור">
            {filterChips.map((chip) => {
              const Icon = chip.icon;
              const isActive = activeSource === chip.id;
              const count = counts[chip.id];
              return (
                <button
                  key={chip.id}
                  type="button"
                  className={`nnf-chip nnf-chip-${chip.id} ${
                    isActive ? "active" : ""
                  }`}
                  aria-pressed={isActive}
                  onClick={() => setActiveSource(chip.id)}
                >
                  <Icon size={13} weight={isActive ? "fill" : "regular"} />
                  <span className="nnf-chip-label">{chip.label}</span>
                  <span className="nnf-chip-count">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="nnf-body">{renderBody()}</div>
      </div>
    </div>
  );
}

export default NutritionNotesFeed;
